import React, { useState } from "react";
import { FilePdf, ArrowSquareOut, X, DownloadSimple } from "@phosphor-icons/react";

export const KYB_DOC_LABELS = {
  incorporation: "Certificate of Incorporation",
  director_id: "Director ID / Passport",
  proof_of_address: "Proof of Address",
  bank_statement: "Bank Statement",
  other: "Other",
};

function docLabel(docType) {
  return KYB_DOC_LABELS[docType] || docType || "Document";
}

function isPdfDoc(doc) {
  const name = (doc.file_name || "").toLowerCase();
  const url = (doc.file_url || "").toLowerCase();
  return name.endsWith(".pdf") || url.includes("/raw/upload");
}

function formatUploaded(at) {
  if (!at) return "—";
  return at.slice(0, 19).replace("T", " ");
}

export function KybDocumentGallery({ documents, emptyMessage = "No documents uploaded." }) {
  const docs = documents || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  if (!docs.length) {
    return <p className="text-sm text-zinc-500 py-6 text-center">{emptyMessage}</p>;
  }

  const active = docs[activeIndex] ?? docs[0];
  const hasPreview = Boolean(active?.file_url);

  const openDoc = (index) => {
    setActiveIndex(index);
    setLightbox(true);
  };

  return (
    <div data-testid="kyb-document-gallery">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {docs.map((d, i) => {
          const pdf = isPdfDoc(d);
          const selected = i === activeIndex;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => openDoc(i)}
              data-testid={`kyb-doc-card-${d.id}`}
              className={`group text-left border rounded-sm overflow-hidden bg-white transition-shadow hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 ${
                selected ? "border-zinc-900 ring-1 ring-zinc-900" : "border-zinc-200"
              }`}
            >
              <div className="aspect-[4/3] bg-zinc-100 relative overflow-hidden">
                {d.file_url && !pdf ? (
                  <img
                    src={d.file_url}
                    alt={d.file_name || docLabel(d.doc_type)}
                    className="h-full w-full object-cover"
                  />
                ) : d.file_url && pdf ? (
                  <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-zinc-600">
                    <FilePdf size={36} weight="duotone" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold">PDF</span>
                  </div>
                ) : (
                  <div className="h-full w-full flex items-center justify-center px-3 text-center text-xs text-zinc-500">
                    No file stored
                  </div>
                )}
                <div className="absolute inset-0 bg-zinc-950/0 group-hover:bg-zinc-950/5 transition-colors" />
              </div>
              <div className="p-2.5 border-t border-zinc-100">
                <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 truncate">
                  {docLabel(d.doc_type)}
                </div>
                <div className="text-xs font-medium text-zinc-900 truncate mt-0.5">{d.file_name}</div>
                <div className="text-[10px] text-zinc-400 mt-1">{formatUploaded(d.uploaded_at)}</div>
              </div>
            </button>
          );
        })}
      </div>

      {hasPreview && (
        <div className="mt-4 border border-zinc-200 rounded-sm bg-zinc-50 overflow-hidden" data-testid="kyb-doc-preview">
          <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-zinc-200 bg-white">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{active.file_name}</div>
              <div className="text-xs text-zinc-500">{docLabel(active.doc_type)}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={active.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="h-8 px-3 inline-flex items-center gap-1.5 text-xs font-medium border border-zinc-200 rounded-sm bg-white hover:bg-zinc-50"
              >
                <ArrowSquareOut size={14} /> Open
              </a>
              <a
                href={active.file_url}
                download={active.file_name || "document"}
                className="h-8 px-3 inline-flex items-center gap-1.5 text-xs font-medium border border-zinc-200 rounded-sm bg-white hover:bg-zinc-50"
              >
                <DownloadSimple size={14} /> Download
              </a>
            </div>
          </div>
          <div className="p-2 min-h-[280px] max-h-[min(70vh,520px)] flex items-center justify-center bg-zinc-100">
            {isPdfDoc(active) ? (
              <iframe
                title={active.file_name || "KYB document"}
                src={active.file_url}
                className="w-full h-[min(70vh,480px)] bg-white rounded-sm border border-zinc-200"
              />
            ) : (
              <img
                src={active.file_url}
                alt={active.file_name || "KYB document"}
                className="max-h-[min(70vh,480px)] max-w-full object-contain rounded-sm"
              />
            )}
          </div>
        </div>
      )}

      {!hasPreview && active && (
        <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
          This document was submitted before file storage was enabled. Ask the merchant to upload it again.
        </p>
      )}

      {lightbox && active?.file_url && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/80"
          role="dialog"
          aria-modal="true"
          data-testid="kyb-doc-lightbox"
          onClick={() => setLightbox(false)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center rounded-sm bg-white/10 text-white hover:bg-white/20"
            onClick={() => setLightbox(false)}
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <div
            className="relative max-w-5xl w-full max-h-[90vh] bg-white rounded-sm overflow-hidden shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{active.file_name}</div>
                <div className="text-xs text-zinc-500">{docLabel(active.doc_type)}</div>
              </div>
              <a
                href={active.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium underline shrink-0"
              >
                Open in new tab
              </a>
            </div>
            <div className="p-2 bg-zinc-100 max-h-[calc(90vh-4rem)] overflow-auto flex justify-center">
              {isPdfDoc(active) ? (
                <iframe
                  title={active.file_name || "KYB document"}
                  src={active.file_url}
                  className="w-full h-[80vh] bg-white"
                />
              ) : (
                <img
                  src={active.file_url}
                  alt={active.file_name || "KYB document"}
                  className="max-h-[80vh] max-w-full object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
